import type React from "react";
import { useRef, useEffect } from "react";

interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  id?: string;
  label?: string;
  showValue?: boolean;
  valueFormatter?: (value: number) => string;
  progressVariable?: string; // CSS variable name for progress (default: --slider-progress)
  disabled?: boolean;
}

function Slider({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  className = "",
  id,
  label,
  showValue = false,
  valueFormatter = (val) => val.toString(),
  progressVariable = "--slider-progress",
  disabled = false,
}: SliderProps): React.JSX.Element {
  const sliderRef = useRef<HTMLInputElement>(null);

  // Update CSS variable for progress visualization
  useEffect(() => {
    if (sliderRef.current) {
      const progress = ((value - min) / (max - min)) * 100;
      sliderRef.current.style.setProperty(progressVariable, `${progress}%`);
    }
  }, [value, min, max, progressVariable]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseInt(e.target.value, 10);
    onChange(newValue);
  };

  return (
    <div className="slider-container">
      {label && (
        <label htmlFor={id} className="slider-label">
          {label}:
        </label>
      )}
      <input
        ref={sliderRef}
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className={`${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      />
      {showValue && (
        <span className="slider-value">
          {valueFormatter(value)}
        </span>
      )}
    </div>
  );
}

export default Slider;
